import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Placeholder text. Defaults to the common `search` string. */
  placeholder?: string;
  /** aria-label for the clear button. Defaults to the common `clearSearch` string. */
  clearAriaLabel?: string;
  /** Stretch to fill the container instead of the default responsive width. */
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

const DEFAULT_SX: SxProps<Theme> = { width: { xs: '100%', md: 500 }, mb: 2 };
const FULL_WIDTH_SX: SxProps<Theme> = { mb: 2 };

/**
 * Shared search input with a leading search icon and a trailing clear button
 * that appears once there is a value. Controlled via `value`/`onChange`.
 * Debouncing is left to the caller (see `useDebounce`). Issue #878.
 */
export function SearchField({
  value,
  onChange,
  placeholder,
  clearAriaLabel,
  fullWidth = false,
  sx,
}: SearchFieldProps) {
  const { t: tc } = useTranslation('common');

  return (
    <TextField
      size="small"
      fullWidth={fullWidth}
      placeholder={placeholder ?? tc('search')}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={sx ?? (fullWidth ? FULL_WIDTH_SX : DEFAULT_SX)}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <Tooltip title={clearAriaLabel ?? tc('clearSearch')}>
                <IconButton
                  size="small"
                  onClick={() => onChange('')}
                  aria-label={clearAriaLabel ?? tc('clearSearch')}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : null,
        },
      }}
    />
  );
}
